/**
 * AttachmentPicker — Auswahl bestehender Dokumente als Anhänge für den Mailversand.
 *
 * Zeigt eine kompakte Liste bereits ausgewählter Anhänge (mit ×-Remove)
 * und einen Button „Anhang hinzufügen", der einen Suchdialog öffnet.
 * Der Dialog listet Dokumente aus dem aktuellen Kontext (Objekt/MV/Kontakt)
 * mit Suchfeld + Kategorien-Filter.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Paperclip, MagnifyingGlass, FilePdf, X, UploadSimple } from "@phosphor-icons/react";
import api from "../lib/api";
import { toast } from "sonner";

export default function AttachmentPicker({
  value = [], onChange,
  context = {}, // { tenancy_id, property_id, contact_id, unit_id }
  currentUserId = null, // iter138 — für „Nur meine Uploads"-Filter
  label = "Anhänge",
  testid = "attachment-picker",
  knownDocs = [], // extern erzeugte Dokumente (z. B. Mietkonto-PDF), damit Chips sofort erscheinen
  extraActions = null,
}) {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const [selectedInDialog, setSelectedInDialog] = useState([]);
  // iter138 — Zwei Toggles, beide standardmäßig AN, wenn ihre Voraussetzung
  // erfüllt ist. Verhindert, dass der User durch fremde Dokumente wühlen muss.
  const hasContact = !!(context.contact_id || context.tenancy_id);
  const [onlyRecipient, setOnlyRecipient] = useState(hasContact);
  const [onlyMine, setOnlyMine] = useState(!!currentUserId);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = {};
    // Grundfilter (Objekt/MV/Einheit) immer mitschicken für Performance.
    if (context.tenancy_id) params.tenancy_id = context.tenancy_id;
    if (context.property_id) params.property_id = context.property_id;
    if (context.unit_id) params.unit_id = context.unit_id;
    api.get("/documents", { params })
      .then(({ data }) => setDocs(Array.isArray(data) ? data : []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
    setSelectedInDialog(value);
    setQuery("");
  }, [open]);  // eslint-disable-line

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const targetContact = context.contact_id;
    return docs
      .filter((d) => {
        if (onlyRecipient && targetContact) {
          if (d.linked_contact_id !== targetContact) return false;
        }
        if (onlyMine && currentUserId) {
          if ((d.created_by_user_id || d.uploaded_by_user_id) !== currentUserId) return false;
        }
        if (!q) return true;
        return (
          (d.title || "").toLowerCase().includes(q) ||
          (d.original_filename || "").toLowerCase().includes(q) ||
          (d.category || "").toLowerCase().includes(q)
        );
      });
  }, [docs, query, onlyRecipient, onlyMine, currentUserId, context.contact_id]);

  const toggleInDialog = (id) => {
    setSelectedInDialog((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  const applySelection = () => {
    onChange(selectedInDialog);
    setOpen(false);
  };

  const removeAttachment = (id) => {
    onChange(value.filter((x) => x !== id));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      fd.append("category", "Anlage");
      fd.append("direction", "out");
      if (context.property_id) fd.append("linked_property_id", context.property_id);
      if (context.unit_id) fd.append("linked_unit_id", context.unit_id);
      if (context.tenancy_id) fd.append("linked_tenancy_id", context.tenancy_id);
      if (context.contact_id) fd.append("linked_contact_id", context.contact_id);
      const { data } = await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Auto-Auswahl des frisch hochgeladenen Dokuments
      setDocs((d) => [data, ...d]);
      setSelectedInDialog((s) => [...s, data.id]);
      toast.success(`„${data.original_filename}" hinzugefügt`);
    } catch (err) {
      toast.error("Upload fehlgeschlagen: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const allDocs = [...docs, ...knownDocs.filter((k) => !docs.some((d) => d.id === k.id))];
  const selectedDocs = allDocs.filter((d) => value.includes(d.id));
  // Falls Docs noch nicht geladen (Dialog nie geöffnet), zeige nur IDs
  const showLoadingChips = value.length > 0 && selectedDocs.length === 0;

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between">
        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          {label} {value.length > 0 && <span className="text-emerald-600 normal-case font-normal">({value.length})</span>}
        </label>
        <div className="flex items-center gap-3">
          {extraActions}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-[10px] font-semibold text-slate-700 hover:text-slate-900 flex items-center gap-1"
            data-testid={`${testid}-open`}
          >
            <Paperclip size={12} /> Hinzufügen
          </button>
        </div>
      </div>
      <div className="mt-1.5 space-y-1">
        {value.length === 0 && (
          <div className="text-[11px] text-slate-400 italic">Keine Anhänge — Brief-PDF wird trotzdem angehängt.</div>
        )}
        {showLoadingChips && value.map((id) => (
          <div key={id} className="flex items-center gap-1.5 text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1 text-slate-500 italic">
            <FilePdf size={11} /> {id.slice(0, 8)}…
          </div>
        ))}
        {selectedDocs.map((d) => (
          <div key={d.id} className="flex items-center gap-1.5 text-[11px] bg-blue-50 border border-blue-200 rounded px-2 py-1"
               data-testid={`${testid}-chip-${d.id}`}>
            <FilePdf size={12} className="text-blue-700 shrink-0" />
            <span className="flex-1 truncate text-slate-800" title={d.title || d.original_filename}>
              {d.title || d.original_filename}
            </span>
            <button
              type="button"
              onClick={() => removeAttachment(d.id)}
              className="text-slate-400 hover:text-red-600 shrink-0"
              data-testid={`${testid}-remove-${d.id}`}
              title="Entfernen"
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle>Anhänge auswählen</DialogTitle>
            <DialogDescription>
              Wähle bestehende Dokumente aus dem Kontext oder lade neue hoch. Der eigentliche Brief wird immer automatisch mitgeschickt.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Suchen (Titel, Dateiname, Kategorie)…"
                className="pl-8 h-9 text-sm"
                data-testid={`${testid}-search`}
              />
            </div>
            <label className="flex items-center gap-1.5 text-xs font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-md px-3 py-2 cursor-pointer">
              <UploadSimple size={14} />
              {uploading ? "Lädt…" : "Hochladen"}
              <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} data-testid={`${testid}-upload`} />
            </label>
          </div>
          {/* iter138 — zwei Filter-Toggles, beide sind smart-Defaults */}
          <div className="flex items-center gap-4 flex-wrap text-xs text-slate-700 pt-1">
            {context.contact_id && (
              <label className="flex items-center gap-2 cursor-pointer" data-testid={`${testid}-filter-recipient`}>
                <Checkbox checked={onlyRecipient} onCheckedChange={(v) => setOnlyRecipient(!!v)} />
                <span>Nur empfängerbezogen</span>
              </label>
            )}
            {currentUserId && (
              <label className="flex items-center gap-2 cursor-pointer" data-testid={`${testid}-filter-mine`}>
                <Checkbox checked={onlyMine} onCheckedChange={(v) => setOnlyMine(!!v)} />
                <span>Nur meine Uploads</span>
              </label>
            )}
            <span className="text-[10px] text-slate-500 ml-auto italic">
              {filtered.length} von {docs.length}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto border border-slate-200 rounded-md">
            {loading ? (
              <div className="p-6 text-center text-sm text-slate-500">Lade Dokumente…</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">
                Keine passenden Dokumente. Nutze &bdquo;Hochladen&ldquo; um eine neue Datei anzuhängen.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {filtered.map((d) => (
                  <li key={d.id}
                      className={`flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer ${selectedInDialog.includes(d.id) ? "bg-blue-50" : ""}`}
                      onClick={() => toggleInDialog(d.id)}
                      data-testid={`${testid}-item-${d.id}`}>
                    <Checkbox checked={selectedInDialog.includes(d.id)} onCheckedChange={() => toggleInDialog(d.id)} />
                    <FilePdf size={14} className="text-slate-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{d.title || d.original_filename}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {d.category} · {d.original_filename} · {(d.size / 1024).toFixed(0)} KB
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={applySelection} className="bg-slate-900 hover:bg-slate-800" data-testid={`${testid}-apply`}>
              {selectedInDialog.length} übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
